import { BiothreatSignalSource } from "@phc/common/gen/threatAnalyticsGen"
import { dayjsUTC } from "../../utils/dates"
import type { BiothreatSignal } from "./AlertCurationContext"
import { faNewspaper, faPipeValve } from "@fortawesome/sharp-solid-svg-icons"

export const DEFAULT_SCORE = 0.5

// TODO: Rename to AlertCandidateSignal once it can be used generically for alert candidates
export interface GdeltSignal {
  id: string
  cluster_id: string
  is_centroid: boolean
  is_archived: boolean
  cluster_count: number
  modern_score: number
  legacy_score: number
  title: string
  url: string
  publish_time: string
  phc_location: string
  source_name: string
  pruned_themes: string[]
  search_term_id?: string
  threat_category?: string
  clean_disease_codes?: string[]
  resolved_location?: string
  is_pinned?: boolean
}

export interface ApiResponse {
  count: number
  results: GdeltSignal[]
}

export interface BatchTriagePayload {
  ids: string[]
  action: "archive" | "unarchive"
}

export interface MutationContext {
  previousData: ApiResponse | undefined
}

export const ALL_DISEASES = "All"
export const ALL_LOCATIONS = "All"

export const ALL_TYPES = "All"
export const TYPE_OSINT = "Public/Commercial Data"
export const TYPE_MEDICAL_SEARCH_ENGINE = "Medical Search Engine"
export const TYPE_NWSS = "National Wastewater Surveillance System"

export const SOURCE_TYPE_TO_LABEL = {
  [BiothreatSignalSource.nwss]: TYPE_NWSS,
  [BiothreatSignalSource.gdelt]: TYPE_OSINT,
}

export const SOURCE_TYPE_TO_ICON = {
  [BiothreatSignalSource.nwss]: faPipeValve,
  [BiothreatSignalSource.gdelt]: faNewspaper,
}

export const ALL_DATA_TYPES = [
  ALL_TYPES,
  TYPE_OSINT,
  TYPE_MEDICAL_SEARCH_ENGINE,
  TYPE_NWSS,
]

export const MOCK_UP_TO_DATE_SIGNALS: BiothreatSignal[] = [
  {
    id: "a1b2c3d4-e5f6-4789-abcd-1234567890ab",
    cluster_id: "mse-flu-ca",
    is_centroid: true,
    is_archived: false,
    cluster_count: 25,
    modern_score: 0.892341562184021,
    legacy_score: 0.215482930128374,
    title:
      "Significant clinical search interest spike for Influenza in California",
    url: "",
    publish_time: "2026-02-09T00:00:00Z",
    resolved_location: "California",
    source_name: "MSE",
    pruned_themes: ["TAX_DISEASE_INFLUENZA"],
    clean_disease_codes: ["Influenza"],
    threat_category: "Infectious Disease",
    source: "nwss",
    description: null,
  },
  {
    id: "b2c3d4e5-f6a7-4890-bcde-2345678901bc",
    cluster_id: "mse-noro-ny",
    is_centroid: true,
    is_archived: false,
    cluster_count: 18,
    modern_score: 0.754128394012831,
    legacy_score: 0.188231940128372,
    title: "Norovirus search activity higher than expectedin New York",
    url: "",
    publish_time: "2026-02-09T00:00:00Z",
    resolved_location: "New York",
    source_name: "MSE",
    pruned_themes: ["TAX_DISEASE_NOROVIRUS"],
    clean_disease_codes: ["Norovirus"],
    threat_category: "Gastrointestinal",
    source: "nwss",
    description: null,
  },
  {
    id: "c3d4e5f6-a7b8-4901-cdef-3456789012cd",
    cluster_id: "mse-rsv-tx",
    is_centroid: true,
    is_archived: false,
    cluster_count: 12,
    modern_score: 0.682193041283712,
    legacy_score: 0.142381049128371,
    title:
      "Abnormal clinical search patterns for Respiratory Syncytial Virus detected in Texas",
    url: "",
    publish_time: "2026-02-09T00:00:00Z",
    resolved_location: "Texas",
    source_name: "MSE",
    pruned_themes: ["TAX_DISEASE_RSV"],
    clean_disease_codes: ["RSV"],
    threat_category: "Infectious Disease",
    source: "nwss",
    description: null,
  },
  {
    id: "d4e5f6a7-b8c9-4012-def0-4567890123de",
    cluster_id: "mse-hfmd-fl",
    is_centroid: true,
    is_archived: false,
    cluster_count: 31,
    modern_score: 0.815239104812731,
    legacy_score: 0.203184920128374,
    title:
      "Clinical search interest spike for Hand Foot and Mouth Disease in Florida",
    url: "",
    publish_time: "2026-02-09T00:00:00Z",
    resolved_location: "Florida",
    source_name: "MSE",
    pruned_themes: ["TAX_DISEASE_HFMD"],
    clean_disease_codes: ["HFMD"],
    threat_category: "Infectious Disease",
    source: "nwss",
    description: null,
  },
]

/**
 * Helper to check if a signal has the required data for display
 */
export const hasRequiredData = (s: BiothreatSignal) =>
  Boolean(s.resolved_location && s.clean_disease_codes.length > 0)

/**
 * Logic exactly preserved from original, but adjusted to make "Ghost Filters" functional:
 * Show unique highest score row for each cluster in the current pool that matches active filters.
 */
export const getCentroidSignals = (
  signals: BiothreatSignal[],
  isArchivedView: boolean,
  locationFilter: string,
  typeFilter: string,
  search = "",
  showNullData = false
): BiothreatSignal[] => {
  const clusterMap: Record<string, BiothreatSignal[]> = {}
  signals.forEach(s => {
    const cid = s.cluster_id
    if (!clusterMap[cid]) {
      clusterMap[cid] = []
    }
    clusterMap[cid].push(s)
  })

  const representatives: BiothreatSignal[] = []
  Object.values(clusterMap).forEach(members => {
    const poolMembers = members.filter(m => {
      const matchesArchive = m.is_archived === isArchivedView
      const matchesLocation =
        locationFilter === ALL_LOCATIONS ||
        m.resolved_location === locationFilter
      const matchesType =
        typeFilter === ALL_TYPES ||
        (typeFilter === TYPE_MEDICAL_SEARCH_ENGINE &&
          m.source_name === "MSE") ||
        (typeFilter === TYPE_OSINT &&
          m.source === BiothreatSignalSource.gdelt) ||
        (typeFilter === TYPE_NWSS && m.source === BiothreatSignalSource.nwss)

      const matchesNull = showNullData || hasRequiredData(m)

      const searchTerms = search.toLowerCase().split(/\s+/).filter(Boolean)
      const matchesSearch =
        searchTerms.length === 0 ||
        searchTerms.every(
          term =>
            m.title.toLowerCase().includes(term) ||
            (m.source_name || "").toLowerCase().includes(term) ||
            (m.resolved_location || "").toLowerCase().includes(term)
        )

      return (
        matchesArchive &&
        matchesLocation &&
        matchesType &&
        matchesNull &&
        matchesSearch
      )
    })

    if (poolMembers.length > 0) {
      // Representative is the leader of the VISIBLE pool
      const leader = poolMembers.reduce((prev, current) =>
        (prev.modern_score ?? DEFAULT_SCORE) >
        (current.modern_score ?? DEFAULT_SCORE)
          ? prev
          : current
      )
      representatives.push({
        ...leader,
        is_centroid: true,
      })
    }
  })

  return representatives
}

export const getExportFileName = () => {
  return `potential_alerts_${dayjsUTC().format("YYYY-MM-DD")}.csv`
}
